import AppVersion from '@/components/app-version'
import { NavbarLogo } from '@/components/custom-ui/3d-cube-logo'
import { FullPageSpinner } from '@/components/loading_states/generic-spinner'
import { useAuth } from '@/components/providers/auth-provider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { logoutAndRedirect } from '@/lib/auth'
import { COLORS, getDisplayEmail } from '@repo/shared_utils'
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import { ArrowRight, LogOut, User, Users } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef } from 'react'

interface RolePortalConfig {
  title: string
  description: string
  path: string
  icon: React.ReactNode
  color: string
  gradient: string
}

const rolePortals: Record<string, RolePortalConfig> = {
  admin: {
    title: 'Admin Portal',
    description: 'Manage system settings, users, and view analytics',
    path: '/dashboard/admin',
    icon: <NavbarLogo aria-hidden="true" className="h-16 w-16" />,
    color: COLORS[0],
    gradient: 'from-blue-500 to-indigo-600',
  },
  hr_admin: {
    title: 'HR Admin Portal',
    description: 'Manage employees, timesheets, and payroll',
    path: '/dashboard/hr_admin',
    icon: <Users className="h-8 w-8" />,
    color: COLORS[1],
    gradient: 'from-indigo-500 to-purple-600',
  },
  // hr_recruitment: {
  //   title: 'HR Recruitment Portal',
  //   description: '201 Files, SSS, Pag-ibig, Tax, Employee Details, etc.',
  //   path: '/dashboard/hr_recruitment',
  //   icon: <Users className="h-8 w-8" />,
  //   color: COLORS[1],
  //   gradient: 'from-indigo-500 to-purple-600',
  // },
  supervisor: {
    title: 'Supervisor Portal',
    description: 'Oversee team timesheets and employee activity',
    path: '/dashboard/supervisor',
    icon: <Users className="h-8 w-8" />,
    color: COLORS[3],
    gradient: 'from-green-500 to-teal-600',
  },
  employee: {
    title: 'Employee Portal',
    description: 'Access your tasks, timesheets, and reports',
    path: '/dashboard/employee',
    icon: <User className="h-8 w-8" />,
    color: COLORS[2],
    gradient: 'from-purple-500 to-violet-600',
  },
  // team_leader: {
  //   title: 'Team Leader Portal',
  //   description: 'Manage your team, assign tasks, and track progress',
  //   path: '/dashboard/team_leader',
  //   icon: <UsersIcon className="h-8 w-8" />,
  //   color: COLORS[3],
  //   gradient: 'from-violet-500 to-purple-600',
  // },
}

export const Route = createFileRoute('/dashboard/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user, isPending } = useAuth()
  const posthog = usePostHog()
  const sessionRecordingStarted = useRef(false)
  const navigate = useNavigate()

  // Derive portal: use user.role if set and in rolePortals; otherwise pick first role from userRoles that has a portal (e.g. dev bypass user has userRoles but null legacy user.role)
  const roleNames = user?.userRoles?.map((r) => r.role.name) ?? []
  const portalRole =
    (user?.role && rolePortals[user.role] ? user.role : null) ??
    (Object.keys(rolePortals) as string[]).find((k) => roleNames.includes(k)) ??
    null
  const userPortal = portalRole ? (rolePortals[portalRole] ?? null) : null

  // // TODO
  // if (!userPortal && user?.role === 'hr_admin') {
  //   // if role is hr_admin, redirect to admin portal
  //   userPortal = rolePortals.admin
  // }

  // Start PostHog session replay when userPortal is not available
  useEffect(() => {
    if (!userPortal && user && posthog && !sessionRecordingStarted.current) {
      try {
        // Identify the user first
        posthog.identify(user.userId, {
          email: user.email,
          name: user.name,
          role: user.role,
          businessId: user.businessId,
          // Additional user properties for better context
          hasValidRole: false,
          currentPage: 'dashboard',
          timestamp: new Date().toISOString(),
        })

        // Start session recording
        posthog.startSessionRecording()
        sessionRecordingStarted.current = true

        // Capture the invalid role configuration event
        posthog.capture('invalid_role_configuration', {
          userId: user.userId,
          userRole: user.role,
          businessId: user.businessId,
          page: 'dashboard',
          timestamp: new Date().toISOString(),
        })

        // // Capture page view for session context
        // posthog.capture('page_viewed', {
        //   page: 'dashboard',
        //   pageTitle: 'Dashboard - Invalid Role',
        //   userId: user.userId,
        //   userRole: user.role,
        // })
      } catch (error) {
        console.error('Failed to start PostHog session recording:', error)
        // Reset the flag if there was an error
        sessionRecordingStarted.current = false
      }
    }

    // Cleanup: stop session recording if userPortal becomes available
    if (userPortal && sessionRecordingStarted.current && posthog) {
      try {
        posthog.stopSessionRecording()
        sessionRecordingStarted.current = false

        // Capture when session recording stops
        posthog.capture('session_recording_stopped', {
          reason: 'valid_role_configured',
          userId: user?.userId,
          userRole: user?.role,
        })
      } catch (error) {
        console.error('Failed to stop PostHog session recording:', error)
        // Reset the flag if there was an error
        sessionRecordingStarted.current = false
      }
    }
  }, [userPortal, user, posthog])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionRecordingStarted.current && posthog) {
        posthog.stopSessionRecording()
        sessionRecordingStarted.current = false
      }
    }
  }, [posthog])

  if (isPending) return <FullPageSpinner />
  if (!user) return <Navigate to="/login" />

  if (!userPortal) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-lg border border-red-200 dark:border-red-800">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-red-600 dark:text-red-400">
              Invalid Role Configuration (role: {user.role})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Your account role is not properly configured. Please contact your administrator.
              (roles: {user?.userRoles?.map((userRole) => userRole.role.name).join(', ')})
            </p>
            {/* Add contact information or action buttons for better user experience */}
            <div className="mt-4 space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (posthog && user) {
                    posthog.capture('contact_admin_clicked', {
                      userId: user.userId,
                      userRole: user.role,
                      businessId: user.businessId,
                      page: 'dashboard',
                    })
                  }
                }}
                className="w-full"
              >
                Contact Administrator
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (posthog && user) {
                    posthog.capture('refresh_page_clicked', {
                      userId: user.userId,
                      userRole: user.role,
                      businessId: user.businessId,
                      page: 'dashboard',
                    })
                    // Refresh the page to check if role was updated
                    window.location.reload()
                  }
                }}
                className="w-full"
              >
                Refresh Page
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 flex items-center justify-center p-4">
      {/* Background decorative elements */}
      <div
        className="absolute top-20 right-20 w-64 h-64 rounded-full opacity-10 blur-3xl"
        style={{ backgroundColor: userPortal.color }}
      />
      <div
        className="absolute bottom-20 left-20 w-64 h-64 rounded-full opacity-10 blur-3xl"
        style={{ backgroundColor: COLORS[4] }}
      />

      <div className="relative">
        <Card className="w-full max-w-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-0 shadow-2xl ring-1 ring-gray-200 dark:ring-gray-800 transition-all duration-300 hover:shadow-3xl">
          <CardHeader className="text-center pb-8 pt-12">
            {/* Icon container */}
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl transition-all duration-300 hover:scale-110"
              style={{ backgroundColor: `${userPortal.color}15` }}
            >
              <div style={{ color: userPortal.color }}>{userPortal.icon}</div>
            </div>

            <CardTitle className="text-3xl font-bold mb-2">
              <span
                className="bg-gradient-to-r bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(to right, ${userPortal.color}, ${COLORS[1]})`,
                }}
              >
                {userPortal.title}
              </span>
            </CardTitle>

            {user && (
              <div
                className="inline-flex truncate items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: `${userPortal.color}10`,
                  color: userPortal.color,
                  border: `1px solid ${userPortal.color}20`,
                }}
              >
                {getDisplayEmail(user.email ?? '', user.maskEmail ?? false)}
              </div>
            )}
          </CardHeader>

          <CardContent className="flex flex-col items-center space-y-8 pb-12">
            <p className="text-center text-lg text-muted-foreground max-w-md leading-relaxed">
              {userPortal.description}
            </p>

            {/* Enhanced button */}
            <div className="w-full max-w-sm flex flex-col space-y-6">
              {/* Split section: Home link and Logout button */}
              <div className="flex gap-3 w-full">
                <Link to="/" className="flex-1">
                  <Button
                    size="lg"
                    className="w-full h-14 text-lg font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl group relative overflow-hidden"
                    style={{ backgroundColor: userPortal.color }}
                  >
                    <span>Home</span>
                  </Button>
                </Link>

                <Button
                  size="lg"
                  className="flex-1 h-14 text-lg font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl group relative overflow-hidden"
                  style={{ backgroundColor: userPortal.color }}
                  onClick={() => logoutAndRedirect(navigate)}
                >
                  <span>Log out</span>
                  <LogOut className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </div>

              <Link to={userPortal.path} className="w-full">
                <Button
                  size="lg"
                  className="w-full h-14 text-lg font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl group relative overflow-hidden"
                  style={{ backgroundColor: userPortal.color }}
                >
                  <div
                    className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${userPortal.color}, ${COLORS[1]})`,
                    }}
                  />
                  <span className="relative flex items-center gap-3 z-1000">
                    Enter Portal
                    <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Button>
              </Link>
            </div>

            {/* Additional info cards */}
            <div className="grid grid-cols-2 gap-4 w-full max-w-md mt-8">
              <div
                className="p-4 rounded-xl text-center transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: `${userPortal.color}08` }}
              >
                <div className="text-2xl font-bold" style={{ color: userPortal.color }}>
                  24/7
                </div>
                <div className="text-sm text-muted-foreground">Support</div>
              </div>
              <div
                className="p-4 rounded-xl text-center transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: `${COLORS[1]}08` }}
              >
                <div className="text-2xl font-bold" style={{ color: COLORS[1] }}>
                  Secure
                </div>
                <div className="text-sm text-muted-foreground">Access</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* App Version */}
      <div
        className="
          fixed
          left-1/2
          -translate-x-1/2
          bottom-3
          md:absolute
          md:left-4
          md:translate-x-0
          md:bottom-4
          z-20
          px-2
          py-1
          rounded-md
          bg-white/80
          dark:bg-background/80
          shadow
          text-xs
          text-muted-foreground
          select-none
          pointer-events-none
        "
        style={{
          maxWidth: '90vw',
          backdropFilter: 'blur(4px)',
        }}
      >
        <AppVersion />
      </div>
    </div>
  )
}
